import { useSetAtom } from 'jotai';
import { useLayoutEffect } from 'react';

import { commitBeforePaint } from '@/lib/commitBeforePaint';
import { calcStageLayout } from '@/state/ui/shellStage';
import { setStageLayoutAtom, setStageResizingAtom } from '@/state/ui/shellStageAtoms';
import type { StageLayout } from '@/types/panels';

// How long the width has to hold still before the stage counts as at rest.
export const RESIZE_DEBOUNCE_MS = 120;

/**
 * Measure the stage's live allocation width in pixels.
 *
 * Live is the whole contract. Panels are laid out for the width the stage has
 * right now, every time it changes. While the chat drawer slides or a
 * splitter is dragged, the drawer/nav is the ONLY animated element — the
 * stage follows it by flex layout, this hook re-measures each change, and the
 * allocator repositions every panel for the measured width with their own
 * edge transitions suppressed (main[data-resizing]). Everything on screen
 * derives from one animated value, so the drawer, the rail, and every panel
 * cannot fall out of step — the property this replaces (allocating once for a
 * predicted settled width and racing a second set of panel transitions
 * against the drawer's) could only approximate.
 *
 * Both measurement paths fire inside the rendering steps, before paint — the
 * resize event at the top of the frame, ResizeObserver after layout — so the
 * synchronous commit (commitBeforePaint) lands the reallocation in the same
 * frame as the stage-width change that caused it, provided PanelStage paints
 * the allocation of the commit it renders in. Otherwise the stage's edge moves
 * every panel first and their widths follow a frame later.
 *
 * The resize flag rides the SAME commit as the width it belongs to. The stage turns
 * its slot easing off while the width moves, and a flag that landed a frame
 * later left that first frame to ease every slot toward the new width, then
 * cut the ease short — each burst of a window drag began with the row lurching
 * sideways and snapping back. It stays up until the width has held still for
 * RESIZE_DEBOUNCE_MS.
 */
export function useStageLayout(ref: React.RefObject<HTMLElement | null>): void {
    const setLayout = useSetAtom(setStageLayoutAtom);
    const setResizing = useSetAtom(setStageResizingAtom);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }

        // The unrounded width: mid-slide the stage sits between pixels, and a rounded one leaves
        // the row's right edge wobbling half a pixel either side of the stage's. A stage with
        // no layout at all reports a zero clientWidth, and the window stands in for it.
        const measure = (): StageLayout => calcStageLayout(
            element.clientWidth > 0 ? element.getBoundingClientRect().width : window.innerWidth,
            window.innerWidth);
        const hasLayoutChanged = (nextLayout: StageLayout, previous: StageLayout): boolean => (
            nextLayout.stageWidth !== previous.stageWidth ||
            nextLayout.isPhone !== previous.isPhone
        );
        // The initial measure — already inside a commit, so no flushSync, and
        // not a movement.
        let lastStageLayout = measure();
        setLayout(lastStageLayout);

        let debounceTimer: ReturnType<typeof setTimeout> | undefined;
        const follow = (): void => {
            const nextStageLayout = measure();
            if (!hasLayoutChanged(nextStageLayout, lastStageLayout)) {
                return;
            }
            lastStageLayout = nextStageLayout;
            setLayout(nextStageLayout);
            setResizing(true);
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout((): void => setResizing(false), RESIZE_DEBOUNCE_MS);
        };
        const followSync = (): void => commitBeforePaint(follow);

        const observer = new ResizeObserver(followSync);
        observer.observe(element);
        window.addEventListener('resize', followSync);
        return (): void => {
            observer.disconnect();
            window.removeEventListener('resize', followSync);
            clearTimeout(debounceTimer);
            // The store outlives the shell: a flag left up keeps the slot easing off on the
            // next mount until the next resize.
            setResizing(false);
        };
    }, [ref, setLayout, setResizing]);
}
